/**
 * @page /portal (Kunden-Portal)
 * @description Kunden-Portal Dashboard — Uebersicht fuer Kunden mit KPIs,
 *   Fulfillment-Ring, Soll/Ist-Chart und aktuelle Performance.
 * @features
 *   - Fulfillment-KPIs (Sendungen, Soll/Ist, Auslastung)
 *   - Ring-Diagramm fuer Erfuellungsgrad
 *   - Soll/Ist-Liniendiagramm (SVG)
 *   - Aktuelle Rechnungs-Uebersicht
 * @api GET /api/portal/dashboard
 * @roles kunde
 * @i18n portalDashboard
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LineChart as RechartsLineChart,
  Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { PageHeader, Badge } from '../components/ui';

const API = import.meta.env.VITE_API_URL || '';
const api = (path) => fetch(`${API}${path}`, { credentials: 'include' }).then(r => r.json());

function KpiSection({ label, children }) {
  return (
    <div className="bg-surface-card border border-line rounded-card p-5 shadow-card">
      <div className="text-[10px] text-[var(--text-secondary)] font-semibold uppercase tracking-wider mb-3">{label}</div>
      {children}
    </div>
  );
}

function formatXAxisDatum(datum) {
  const d = new Date(datum);
  return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.`;
}

function LineChart({ data, noDataText, t }) {
  if (!data || data.length < 2) {
    return <div className="text-xs text-[var(--text-secondary)] py-8 text-center">{noDataText}</div>;
  }

  const hasVerf = data.some(d => (d.verfuegbar || 0) > 0);
  const sollLabel = t ? t('customerPortal.dashboard.soll') : 'SOLL';
  const istLabel = t ? t('customerPortal.dashboard.ist') : 'IST';
  const verfLabel = t ? t('portal.available') : 'Verfügbar';

  return (
    <div className="w-full" style={{ height: 320 }}>
      <ResponsiveContainer width="100%" height="100%">
        <RechartsLineChart data={data} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
          <XAxis dataKey="datum" tickFormatter={formatXAxisDatum} stroke="#6B7280" fontSize={12} minTickGap={20} />
          <YAxis stroke="#6B7280" fontSize={12} />
          <Tooltip
            labelFormatter={(v) => new Date(v).toLocaleDateString('de-AT', { weekday: 'short', day: '2-digit', month: '2-digit' })}
            formatter={(value) => value?.toLocaleString('de-DE')}
            contentStyle={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-card)', borderRadius: 8, fontSize: 12 }}
          />
          <Line type="monotone" dataKey="soll" stroke="#338edc" name={sollLabel} strokeWidth={2} dot={false} />
          <Line type="monotone" dataKey="ist" stroke="#10b981" name={istLabel} strokeWidth={2} dot={false} />
          {hasVerf && (
            <Line type="monotone" dataKey="verfuegbar" stroke="#a855f7" strokeDasharray="5 5" name={verfLabel} strokeWidth={2} dot={false} />
          )}
        </RechartsLineChart>
      </ResponsiveContainer>
    </div>
  );
}

const MONTH_KEYS = [0,1,2,3,4,5,6,7,8,9,10,11];

export function ForecastBanner({ t: tProp }) {
  const { t: tHook } = useLanguage();
  const t = tProp || tHook;
  const navigate = useNavigate();
  const [forecasts, setForecasts] = useState([]);

  useEffect(() => {
    fetch(`${API}/api/portal/customer-forecasts`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : [])
      .then(data => setForecasts(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, []);

  // Nur zukünftige/aktuelle Forecasts (nächste 3 Monate)
  const now = new Date();
  const currentMonth = now.getFullYear() * 12 + now.getMonth();
  const relevant = forecasts.filter(f => {
    const fcMonth = f.year * 12 + (f.month - 1);
    return fcMonth >= currentMonth && fcMonth <= currentMonth + 3;
  });

  if (relevant.length === 0) return null;

  const actionNeeded = relevant.filter(f => ['draft', 'rejected'].includes(f.status));
  const submitted = relevant.filter(f => ['submitted', 'in_review'].includes(f.status));
  const approved = relevant.filter(f => f.status === 'approved');
  const overdue = actionNeeded.filter(f => f.deadline && new Date(f.deadline) < now);
  const isOverdue = overdue.length > 0;

  // Grüne Variante: Alles eingereicht/freigegeben, nichts offen
  if (actionNeeded.length === 0 && (submitted.length > 0 || approved.length > 0)) {
    const allApproved = submitted.length === 0 && approved.length > 0;
    return (
      <div className="p-4 rounded-xl border flex items-center justify-between gap-4 bg-emerald-50 border-emerald-200 dark:bg-emerald-900/20 dark:border-emerald-800">
        <div className="flex items-center gap-3 min-w-0">
          <svg className="w-5 h-5 text-emerald-600 dark:text-emerald-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div className="min-w-0">
            <div className="text-sm font-semibold text-emerald-800 dark:text-emerald-200">
              {allApproved
                ? t('portal.forecastBanner.approved', { count: approved.length })
                : t('portal.forecastBanner.submitted', { count: submitted.length })
              }
            </div>
            <div className="text-xs text-emerald-600 dark:text-emerald-300">
              {(allApproved ? approved : submitted).map(f => `${t(`customerPortal.months.${f.month - 1}`)} ${f.year}`).join(', ')}
            </div>
          </div>
        </div>
        <button onClick={() => navigate('/portal/forecasts')}
          className="px-4 py-2 text-xs font-semibold rounded-lg whitespace-nowrap transition-colors bg-emerald-600 text-white hover:bg-emerald-700">
          {t('portal.forecastBanner.viewDetails')}
        </button>
      </div>
    );
  }

  // Gelbe/Rote Variante: Noch Forecasts offen
  if (actionNeeded.length === 0) return null;

  return (
    <div className={`p-4 rounded-xl border flex items-center justify-between gap-4 ${
      isOverdue
        ? 'bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-800'
        : 'bg-amber-50 border-amber-200 dark:bg-amber-900/20 dark:border-amber-800'
    }`}>
      <div className="flex items-center gap-3 min-w-0">
        <svg className={`w-5 h-5 flex-shrink-0 ${isOverdue ? 'text-red-600 dark:text-red-400' : 'text-amber-600 dark:text-amber-400'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4.5c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
        </svg>
        <div className="min-w-0">
          <div className={`text-sm font-semibold ${isOverdue ? 'text-red-800 dark:text-red-200' : 'text-amber-800 dark:text-amber-200'}`}>
            {isOverdue
              ? t('portal.forecastBanner.overdue', { count: overdue.length })
              : t('portal.forecastBanner.pending', { count: actionNeeded.length })
            }
          </div>
          <div className={`text-xs ${isOverdue ? 'text-red-600 dark:text-red-300' : 'text-amber-600 dark:text-amber-300'}`}>
            {actionNeeded.map(f => `${t(`customerPortal.months.${f.month - 1}`)} ${f.year}`).join(', ')}
          </div>
        </div>
      </div>
      <button onClick={() => {
          const first = (isOverdue ? overdue : actionNeeded)[0];
          navigate(`/portal/forecasts?open=${first.year}-${first.month}`);
        }}
        className={`px-4 py-2 text-xs font-semibold rounded-lg whitespace-nowrap transition-colors ${
          isOverdue
            ? 'bg-red-600 text-white hover:bg-red-700'
            : 'bg-amber-600 text-white hover:bg-amber-700'
        }`}>
        {t('portal.forecastBanner.enterNow')}
      </button>
    </div>
  );
}

const CATEGORY_I18N = { Handling: 'billingTracker.categoryHandling', Versand: 'billingTracker.categoryVersand', Verpackung: 'billingTracker.categoryVerpackung' };

function BillingSparkline({ data, width = 500, height = 60 }) {
  if (!data || data.length < 2) return null;
  const max = Math.max(...data.map(d => d.cumulative), 1);
  const pad = { left: 40, right: 10, top: 5, bottom: 5 };
  const w = width - pad.left - pad.right;
  const h = height - pad.top - pad.bottom;
  const pts = data.map((d, i) => {
    const x = pad.left + (i / (data.length - 1)) * w;
    const y = pad.top + h - (d.cumulative / max) * h;
    return `${i === 0 ? 'M' : 'L'}${x},${y}`;
  }).join(' ');
  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="w-full" preserveAspectRatio="xMidYMid meet">
      <path d={`${pts} L${pad.left + w},${pad.top + h} L${pad.left},${pad.top + h} Z`} fill="#338edc" fillOpacity={0.08} />
      <path d={pts} fill="none" stroke="#338edc" strokeWidth={2} strokeLinejoin="round" />
    </svg>
  );
}

function PortalBillingTracker() {
  const { t } = useLanguage();
  const [bt, setBt] = useState(null);
  const [btLoading, setBtLoading] = useState(true);

  useEffect(() => {
    api('/api/portal/kunden/billing-tracker')
      .then(setBt).catch(() => setBt(null)).finally(() => setBtLoading(false));
  }, []);

  if (btLoading) {
    return (
      <div className="bg-surface-card border border-line rounded-card p-5 shadow-card animate-pulse">
        <div className="h-3 w-32 bg-surface-inset rounded mb-4" />
        <div className="grid grid-cols-3 gap-3">
          <div className="h-16 bg-surface-inset rounded" />
          <div className="h-16 bg-surface-inset rounded" />
          <div className="h-16 bg-surface-inset rounded" />
        </div>
      </div>
    );
  }

  if (!bt || !bt.ok || bt.current_month?.order_count === 0) return null;

  const cm = bt.current_month;
  const trend = bt.trend;
  const period = bt.period;
  const fmtE = (n) => n == null ? '–' : n.toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + ' €';
  const trendColor = trend.vs_previous_pct == null ? '' : trend.vs_previous_pct >= 0 ? 'text-green-500' : 'text-red-500';
  const trendSign = trend.vs_previous_pct >= 0 ? '+' : '';

  return (
    <div className="bg-surface-card border border-line rounded-card p-5 shadow-card space-y-4">
      <div className="text-sm font-semibold text-content">{t('billingTracker.portalTitle')} {period.month_label}</div>

      {/* 3 KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <KpiSection label={t('billingTracker.totalNetto')}>
          <div className="text-xl font-semibold font-mono text-content">{fmtE(cm.total_netto)}</div>
          <div className="text-[10px] text-[var(--text-secondary)] mt-1">{t('billingTracker.dayProgress', { current: period.days_elapsed, total: period.days_total })}</div>
        </KpiSection>
        <KpiSection label={t('billingTracker.orderCount')}>
          <div className="text-xl font-semibold font-mono text-content">{cm.order_count.toLocaleString('de-DE')}</div>
        </KpiSection>
        <KpiSection label={t('billingTracker.trend')}>
          <div className="flex items-center gap-2">
            <span className={`text-xl font-semibold font-mono text-content`}>
              {trend.vs_previous_pct != null ? `${trendSign}${trend.vs_previous_pct}%` : '–'}
            </span>
            {trend.vs_previous_pct != null && (
              <span className={`text-[10px] ${trendColor}`}>{t('billingTracker.trendNote', { days: period.days_elapsed })}</span>
            )}
          </div>
        </KpiSection>
      </div>

      {/* Category bars */}
      {cm.by_category.length > 0 && (
        <div className="space-y-2">
          {cm.by_category.map(cat => (
            <div key={cat.category} className="flex items-center gap-3">
              <div className="w-[70px] text-[11px] text-[var(--text-secondary)] truncate">{t(CATEGORY_I18N[cat.category] || cat.category)}</div>
              <div className="flex-1 h-[5px] bg-surface-inset rounded-full overflow-hidden">
                <div className="h-full rounded-full bg-[#338edc] transition-all" style={{ width: `${Math.min(cat.pct, 100)}%` }} />
              </div>
              <div className="text-[11px] font-mono font-semibold text-content w-[60px] text-right">{fmtE(cat.amount)}</div>
              <div className="text-[10px] text-[var(--text-tertiary)] w-[30px] text-right">{cat.pct}%</div>
            </div>
          ))}
        </div>
      )}

      {/* Sparkline */}
      {bt.daily_breakdown.length >= 2 && (
        <BillingSparkline data={bt.daily_breakdown} />
      )}
    </div>
  );
}

function QualityScoreCard({ quality, t }) {
  if (!quality || quality.quality_score === null) return null;

  const score = quality.quality_score;
  const label = quality.label;
  const labelKey = `portal.qualityLabel.${label}`;
  const barColor = score >= 90 ? '#34d399' : score >= 70 ? '#338edc' : score >= 50 ? '#f59e0b' : '#ef4444';

  return (
    <KpiSection label={t('portal.forecastQuality')}>
      <div className="flex items-center gap-3">
        <div className="text-2xl font-semibold font-mono text-content">{score}</div>
        <span className="text-xs text-[var(--text-secondary)]">/100</span>
      </div>
      <div className="w-full h-1 bg-surface-inset rounded-full mt-2 overflow-hidden">
        <div className="h-full rounded-full transition-all" style={{ width: `${score}%`, backgroundColor: barColor }} />
      </div>
      <div className="text-[10px] text-[var(--text-secondary)] mt-1.5">{t(labelKey)}</div>
    </KpiSection>
  );
}

function EmpfehlungCard({ quality, t }) {
  if (!quality || !quality.empfehlung) return null;
  const emp = quality.empfehlung;

  let text;
  if (emp.type === 'excellent') {
    text = t('portal.empfehlung.excellent');
  } else if (emp.type === 'too_high') {
    text = t('portal.empfehlung.tooHigh', { bias: emp.bias, reduktion: emp.reduktion, aktuell: emp.aktuell?.toLocaleString('de-DE'), neu: emp.neuer_wert?.toLocaleString('de-DE') });
  } else if (emp.type === 'too_low') {
    text = t('portal.empfehlung.tooLow', { bias: emp.bias, erhoehung: emp.erhoehung, aktuell: emp.aktuell?.toLocaleString('de-DE'), neu: emp.neuer_wert?.toLocaleString('de-DE') });
  } else {
    text = t('portal.empfehlung.acceptable', { accuracy: emp.accuracy });
  }

  return (
    <div className="bg-surface-card border border-line rounded-card p-5 shadow-card">
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-lg bg-amber-500/10 flex items-center justify-center flex-shrink-0 mt-0.5">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
          </svg>
        </div>
        <div className="min-w-0">
          <div className="text-sm font-semibold text-content mb-1">{t('portal.recommendation')}</div>
          <div className="text-xs text-[var(--text-secondary)] leading-relaxed">{text}</div>
          {quality.accuracy != null && (
            <div className="flex gap-4 mt-2 text-[10px] text-[var(--text-tertiary)]">
              <span>{t('portal.empfehlung.accuracyLabel')}: {quality.accuracy}%</span>
              {quality.bias != null && <span>Bias: {quality.bias > 0 ? '+' : ''}{quality.bias}%</span>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function PortalDashboard() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [data, setData] = useState(null);
  const [quality, setQuality] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api('/api/portal/dashboard'),
      api('/api/portal/forecast-quality').catch(() => null),
    ]).then(([d, q]) => {
      setData(d);
      setQuality(q);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin h-8 w-8 border-2 border-[#338edc] border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!data) {
    return <div className="p-6 text-[var(--text-secondary)] text-sm">{t('customerPortal.dashboard.loadError')}</div>;
  }

  const now = new Date();

  // Forecast status badge (summary-based)
  const fcStatus = data.forecast_status;
  const fcSummaryCfg = {
    overdue: { bg: 'bg-red-500/10', text: 'text-red-600 dark:text-red-400', label: t('portal.dashboard.forecastOverdue') },
    open: { bg: 'bg-amber-500/10', text: 'text-amber-600 dark:text-amber-400', label: t('portal.dashboard.forecastDue') },
    submitted: { bg: 'bg-blue-500/10', text: 'text-blue-600 dark:text-blue-400', label: t('portal.dashboard.forecastSubmitted') },
    all_current: { bg: 'bg-emerald-500/10', text: 'text-emerald-600 dark:text-emerald-400', label: t('portal.dashboard.allUpToDate') },
  };
  const fcCfg = fcStatus ? (fcSummaryCfg[fcStatus.summary] || fcSummaryCfg.open) : null;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <PageHeader title={`${t('customerPortal.dashboard.welcome', { name: user?.vorname || data.kunde_name })}${data.kunde_name ? ' · ' + data.kunde_name : ''}`} subtitle={`${t(`customerPortal.months.${now.getMonth()}`)} ${now.getFullYear()}`} />

      {/* KPI Cards */}
      <div className={`grid grid-cols-1 sm:grid-cols-2 ${quality?.quality_score != null ? 'lg:grid-cols-5' : 'lg:grid-cols-4'} gap-3`}>
        {/* Pakete (from pack_stats) */}
        <KpiSection label={t('customerPortal.dashboard.packagesThisMonth')}>
          <div className="text-xl font-semibold font-mono text-content">{(data.packages_this_month || 0).toLocaleString('de-DE')}</div>
          <div className="text-[10px] text-[var(--text-secondary)] mt-1">{t(`customerPortal.months.${now.getMonth()}`)} {now.getFullYear()}</div>
        </KpiSection>

        {/* Forecast Status */}
        <KpiSection label={t('customerPortal.dashboard.forecastStatus')}>
          {fcStatus ? (
            <div>
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ${fcCfg.bg} ${fcCfg.text}`}>
                {fcCfg.label}
              </span>
              {fcStatus.count > 1 && (
                <span className="ml-1 text-[10px] text-[var(--text-secondary)]">({fcStatus.count})</span>
              )}
              {fcStatus.deadline && ['overdue', 'open'].includes(fcStatus.summary) && (
                <div className="text-[10px] text-[var(--text-secondary)] mt-2">
                  Deadline: {new Date(fcStatus.deadline).toLocaleDateString('de-AT')}
                </div>
              )}
            </div>
          ) : (
            <div className="text-sm text-[var(--text-secondary)]">{'\u2013'}</div>
          )}
        </KpiSection>

        {/* Ø Pakete/Tag */}
        <KpiSection label={t('customerPortal.dashboard.avgPerDay')}>
          <div className="text-xl font-semibold font-mono text-content">{(data.avg_per_day || 0).toLocaleString('de-DE')}</div>
          <div className="text-[10px] text-[var(--text-secondary)] mt-1">{data.active_days || 0} {t('portal.stats.activeDays')}</div>
        </KpiSection>

        {/* Offene Rechnungen */}
        <KpiSection label={t('customerPortal.dashboard.openInvoices')}>
          <div className="text-xl font-semibold font-mono text-content">{data.offene_rechnungen.anzahl}</div>
          <div className="text-sm text-[var(--text-primary)] font-mono mt-1">
            {data.offene_rechnungen.summe.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}
          </div>
          <div className="text-[10px] text-[var(--text-secondary)] mt-1">{t('customerPortal.dashboard.open')}</div>
        </KpiSection>

        {/* Quality Score */}
        <QualityScoreCard quality={quality} t={t} />
      </div>

      {/* Billing Tracker */}
      <PortalBillingTracker />

      {/* Chart: SOLL vs IST letzte 30 Tage */}
      <div className="bg-surface-card border border-line rounded-card p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-semibold text-content">{t('customerPortal.dashboard.sollVsIst')}</h2>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-0.5 bg-[#338edc] rounded" />
              <span className="text-[10px] text-[var(--text-secondary)]">{t('customerPortal.dashboard.soll')}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-0.5 bg-emerald-400 rounded" />
              <span className="text-[10px] text-[var(--text-secondary)]">{t('customerPortal.dashboard.ist')}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-[2px] border-t-2 border-dashed border-[#6366F1]" />
              <span className="text-[10px] text-[var(--text-secondary)]">{t('portal.available')}</span>
            </div>
          </div>
        </div>
        <LineChart data={data.chart_30_tage} noDataText={t('customerPortal.dashboard.noData')} t={t} />
      </div>

      {/* Empfehlung Card */}
      <EmpfehlungCard quality={quality} t={t} />

      {/* Nächster Forecast */}
      {data.naechster_forecast && (
        <div className="bg-surface-card border border-line rounded-card p-5 shadow-card">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#338edc]/10 flex items-center justify-center">
              <svg width="20" height="20" fill="none" stroke="#338edc" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                <path d="M3 3v18h18" /><path d="M7 16l4-4 4 4 5-8" />
              </svg>
            </div>
            <div>
              <div className="text-sm font-semibold text-content">
                {t('customerPortal.dashboard.nextForecast', { month: t(`customerPortal.months.${data.naechster_forecast.monat - 1}`), year: data.naechster_forecast.jahr })}
              </div>
              <div className="text-xs text-[var(--text-secondary)]">
                {t('customerPortal.dashboard.packagesPlanned', { count: data.naechster_forecast.pakete_gesamt.toLocaleString('de-DE') })}
                <Badge variant={data.naechster_forecast.status === 'bestaetigt' ? 'success' : data.naechster_forecast.status === 'verteilt' ? 'info' : 'default'} className="ml-2">
                  {data.naechster_forecast.status}
                </Badge>
              </div>
              {quality?.empfehlung?.neuer_wert && quality.empfehlung.neuer_wert !== data.naechster_forecast.pakete_gesamt && (
                <div className="text-[10px] text-amber-600 dark:text-amber-400 mt-1.5 flex items-center gap-1">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                  {t('portal.recommendedValue', { value: quality.empfehlung.neuer_wert.toLocaleString('de-DE') })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
